import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, ShieldCheck, Phone } from "lucide-react";
import api from "../lib/api";
import { formatRupiah, formatNumber, statusClass } from "../lib/format";
import { Skeleton, EmptyState, Section } from "../components/UI";

export default function SupplierDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get(`/suppliers/${id}`),
          api.get("/products", { params: { supplier_id: id } }),
        ]);
        setSupplier(s.data);
        setProducts(p.data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;
  if (!supplier) return <EmptyState title="Supplier tidak ditemukan" />;

  return (
    <div className="space-y-6" data-testid="supplier-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Kembali
      </button>

      <div className="kedan-card p-6">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-lg bg-blue-50 text-[#045498] flex items-center justify-center text-2xl font-display font-bold">
            {supplier.nama_usaha[0]}
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-display font-bold text-slate-900">{supplier.nama_usaha}</h1>
              {supplier.verified && (
                <span className="kedan-badge status-aktif"><ShieldCheck className="w-3 h-3 mr-0.5" />Terverifikasi</span>
              )}
            </div>
            <div className="text-sm text-slate-500 mt-1">{(supplier.jenis_produk || []).join(", ")}</div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-slate-400" />
                {supplier.kota}
                {supplier.kecamatan && `, Kec. ${supplier.kecamatan}`}
                {supplier.kelurahan && `, Kel. ${supplier.kelurahan}`}
              </span>
              <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-400" />{supplier.no_hp}</span>
            </div>
            <div className="text-xs text-slate-500 mt-3">{supplier.alamat}</div>
          </div>
        </div>
      </div>

      <Section title="Katalog Produk" subtitle={`${products.length} item tersedia`}>
        {products.length === 0 ? (
          <EmptyState title="Belum ada produk" description="Supplier ini belum mendaftarkan komoditas." />
        ) : (
          <div className="kedan-card overflow-hidden">
            <table className="kedan-table">
              <thead><tr>
                <th>Produk</th><th>Kategori</th>
                <th className="text-right">Harga</th>
                <th>Satuan</th>
                <th className="text-right">Stok</th>
                <th>Status</th>
              </tr></thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id}>
                    <td className="font-medium">{p.nama}</td>
                    <td>{p.kategori}</td>
                    <td className="text-right font-mono">{formatRupiah(p.harga)}</td>
                    <td>{p.satuan}</td>
                    <td className="text-right font-mono">{formatNumber(p.stok)}</td>
                    <td><span className={`kedan-badge ${statusClass(p.status)}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
